import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, BookOpen, Building2, ArrowRightLeft } from 'lucide-react';
import type { ScoutedFile } from '../../utils/gmailScout';
import type { CompanyOffice } from '../../hooks/useCompanyOffices';

export interface LoanBookGroup {
  id: string;
  entityCode: string | null;
  loanBookName: string;
  officeId: string | null;
  officeName: string | null;
  files: ScoutedFile[];
  isExpanded: boolean;
}

interface LoanBookCardProps {
  group: LoanBookGroup;
  index: number;
  offices: CompanyOffice[];
  allGroups: LoanBookGroup[];
  onUpdateName: (name: string) => void;
  onUpdateOffice: (officeId: string, officeName: string) => void;
  onMoveFile: (fileKey: string, targetGroupId: string) => void;
  onToggleExpand: () => void;
  onCreateOffice: (name: string) => Promise<CompanyOffice>;
}

function fileKey(f: ScoutedFile): string {
  return `${f.messageId}_${f.attachmentId}`;
}

const ACCENT_COLORS = [
  { border: 'border-blue-500/40', bg: 'bg-blue-500/8', badge: 'bg-blue-500/20 text-blue-300', glow: 'shadow-blue-500/10' },
  { border: 'border-emerald-500/40', bg: 'bg-emerald-500/8', badge: 'bg-emerald-500/20 text-emerald-300', glow: 'shadow-emerald-500/10' },
  { border: 'border-violet-500/40', bg: 'bg-violet-500/8', badge: 'bg-violet-500/20 text-violet-300', glow: 'shadow-violet-500/10' },
  { border: 'border-amber-500/40', bg: 'bg-amber-500/8', badge: 'bg-amber-500/20 text-amber-300', glow: 'shadow-amber-500/10' },
  { border: 'border-cyan-500/40', bg: 'bg-cyan-500/8', badge: 'bg-cyan-500/20 text-cyan-300', glow: 'shadow-cyan-500/10' },
  { border: 'border-rose-500/40', bg: 'bg-rose-500/8', badge: 'bg-rose-500/20 text-rose-300', glow: 'shadow-rose-500/10' },
];

export const LoanBookCard: React.FC<LoanBookCardProps> = ({
  group,
  index,
  offices,
  allGroups,
  onUpdateName,
  onUpdateOffice,
  onMoveFile,
  onToggleExpand,
  onCreateOffice,
}) => {
  const [creatingOffice, setCreatingOffice] = useState(false);
  const [newOfficeName, setNewOfficeName] = useState('');
  const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];

  const handleCreateOffice = async () => {
    if (!newOfficeName.trim()) return;
    const office = await onCreateOffice(newOfficeName.trim());
    onUpdateOffice(office.id, office.name);
    setNewOfficeName('');
    setCreatingOffice(false);
  };

  const otherGroups = allGroups.filter((g) => g.id !== group.id);
  const hasName = group.loanBookName.trim().length > 0;
  const hasOffice = !!group.officeId;
  const isComplete = hasName && hasOffice;

  return (
    <div className={`border ${accent.border} rounded-2xl ${accent.bg} overflow-hidden transition-all duration-200 hover:shadow-lg ${accent.glow}`}>
      {/* Top accent line */}
      <div className={`h-0.5 ${accent.border.replace('border-', 'bg-').replace('/40', '/60')}`} />

      {/* Header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gray-800/80 flex items-center justify-center">
              <BookOpen size={16} className="text-gray-300" />
            </div>
            {group.entityCode && (
              <span className={`px-2.5 py-0.5 ${accent.badge} text-xs font-mono rounded-full font-medium`}>
                {group.entityCode}
              </span>
            )}
            <span className="text-sm text-gray-400">
              {group.files.length} {group.files.length === 1 ? 'file' : 'files'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && (
              <span className="text-xs text-green-400 font-medium">Ready</span>
            )}
            <button
              onClick={onToggleExpand}
              className="text-gray-400 hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-700/50 transition-colors"
            >
              {group.isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {/* Form fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Loan book name */}
          <div>
            <label className="block text-xs text-gray-400 mb-1 font-medium">
              Loan Book Name
            </label>
            <input
              type="text"
              value={group.loanBookName}
              onChange={(e) => onUpdateName(e.target.value)}
              placeholder="e.g. Brisbane Loans, QLD Office..."
              className={`w-full bg-gray-900/50 border ${!hasName ? 'border-amber-500/40' : 'border-gray-600'} rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors`}
            />
            {!hasName && (
              <p className="text-[11px] text-amber-400 mt-1">Give this loan book a name your team will recognise</p>
            )}
          </div>

          {/* Office selection */}
          <div>
            <label className="block text-xs text-gray-400 mb-1 font-medium">
              <Building2 size={11} className="inline mr-1 mb-0.5" />
              Office
            </label>
            {creatingOffice ? (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={newOfficeName}
                  onChange={(e) => setNewOfficeName(e.target.value)}
                  placeholder="Office name..."
                  autoFocus
                  className="flex-1 bg-gray-900/50 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateOffice(); if (e.key === 'Escape') setCreatingOffice(false); }}
                />
                <button
                  onClick={handleCreateOffice}
                  disabled={!newOfficeName.trim()}
                  className="px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => setCreatingOffice(false)}
                  className="px-2 py-2 text-gray-400 text-xs hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <select
                  value={group.officeId || ''}
                  onChange={(e) => {
                    const office = offices.find((o) => o.id === e.target.value);
                    if (office) onUpdateOffice(office.id, office.name);
                  }}
                  className={`flex-1 bg-gray-900/50 border ${!hasOffice ? 'border-amber-500/40' : 'border-gray-600'} rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors`}
                >
                  <option value="">Choose an office...</option>
                  {offices.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}{o.isHeadOffice ? ' (HQ)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setCreatingOffice(true)}
                  className="px-2.5 py-2 text-blue-400 hover:text-blue-300 flex items-center gap-1 text-xs whitespace-nowrap border border-gray-600 rounded-lg hover:bg-gray-800/50 transition-colors"
                  title="Create a new office"
                >
                  <Plus size={12} /> New
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File list (collapsible) */}
      {group.isExpanded && (
        <div className="border-t border-gray-700/50 bg-gray-900/30">
          <div className="px-5 py-2">
            <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium mb-1">Files in this loan book</p>
          </div>
          <div className="divide-y divide-gray-700/30">
            {group.files.map((file) => (
              <div
                key={fileKey(file)}
                className="flex items-center gap-2.5 px-5 py-2 text-sm hover:bg-gray-800/30 transition-colors"
              >
                <span className="text-gray-300 truncate flex-1">{file.filename}</span>
                {file.fileType === 'rcti' && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/20">
                    RCTI
                  </span>
                )}
                <span className="text-xs text-gray-500 whitespace-nowrap tabular-nums">
                  {(file.size / 1024).toFixed(1)} KB
                </span>
                {otherGroups.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) onMoveFile(fileKey(file), e.target.value);
                    }}
                    title="Move file to another loan book"
                    className="w-8 h-6 bg-transparent border border-gray-700 rounded text-[10px] text-gray-500 hover:border-gray-500 transition-colors cursor-pointer appearance-none text-center"
                  >
                    <option value="">&#x21C4;</option>
                    {otherGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.loanBookName || g.entityCode || 'Unnamed'}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
